/**
 * Which layers repaint, and when (docs/03-ARCHITECTURE.md §7.1).
 *
 * The whole point of four canvases is that a layer only redraws when its OWN content
 * changes: one pen movement repaints the thin active layer and leaves thousands of
 * finished strokes on the static layer untouched. That is what holds 60fps on a 4 GB
 * Chromebook, so this file keeps the layers strictly independent.
 *
 * Painting is always deferred to the next frame — never run straight out of a pointer
 * event, where several invalidations in one frame would each cost a full repaint.
 */

import type { CanvasLayerName } from '@opal/contracts';

export type Paint = () => void;

/** Runs a callback on the next animation frame and can cancel it again. */
export interface FrameScheduler {
  request(run: () => void): number;
  cancel(handle: number): void;
}

export const animationFrames: FrameScheduler = {
  request: (run) => window.requestAnimationFrame(run),
  cancel: (handle) => {
    window.cancelAnimationFrame(handle);
  },
};

export interface PaintScheduler {
  /** Registers what to run when a layer repaints. */
  setPainter(layer: CanvasLayerName, paint: Paint): void;
  /** Marks one layer as needing a repaint on the next frame. */
  invalidate(layer: CanvasLayerName): void;
  /** Repaints every layer that is waiting. Called on the frame, or by tests. */
  flush(): void;
  /** True while a layer is waiting for its frame. */
  isPending(layer: CanvasLayerName): boolean;
  /** How many times a layer has actually repainted. */
  paintCount(layer: CanvasLayerName): number;
  dispose(): void;
}

export function createPaintScheduler(frames: FrameScheduler = animationFrames): PaintScheduler {
  const painters = new Map<CanvasLayerName, Paint>();
  const pending = new Set<CanvasLayerName>();
  const counts = new Map<CanvasLayerName, number>();
  let frame: number | null = null;

  const flush = (): void => {
    frame = null;
    const due = [...pending];
    pending.clear();
    for (const layer of due) {
      painters.get(layer)?.();
      counts.set(layer, (counts.get(layer) ?? 0) + 1);
    }
  };

  return {
    setPainter(layer, paint) {
      painters.set(layer, paint);
    },
    invalidate(layer) {
      pending.add(layer);
      frame ??= frames.request(flush);
    },
    flush,
    isPending: (layer) => pending.has(layer),
    paintCount: (layer) => counts.get(layer) ?? 0,
    dispose() {
      if (frame !== null) frames.cancel(frame);
      frame = null;
      pending.clear();
      painters.clear();
    },
  };
}
